/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { HotkeysProvider } from '@tanstack/react-hotkeys';
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useState } from 'react';
import './App.css';
import { useMsal } from '@azure/msal-react';
import { Box, StyledEngineProvider, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license';
import type { EnvInfo } from '@poweralpha/common';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import { AppToastContainers } from './AppToastContainers';
import { AgentPanelProvider } from './agent/AgentPanelProvider';
import { clearAgentTabSession } from './agent/agentTabSession';
import AgentLauncherChip from './agent/launcher/AgentLauncherChip';
import { useAgentOnlyLayout } from './agent/useAgentOnlyLayout';
import { getEnv } from './api';
import { DesktopProvider } from './desktop/DesktopProvider';
import { getShellActionGroupSx } from './desktop/glass';
import { appHotkeys } from './desktop/hotkeys/appHotkeys';
import { DesktopShellRouter } from './desktop/navigation/DesktopShellRouter';
import { PreferredThemeProvider } from './desktop/preferences/PreferredThemeProvider';
import { UserPreferencesProvider } from './desktop/preferences/UserPreferencesProvider';
import { lazy } from './lazy';
import NotificationCenter from './notifications/NotificationCenter';
import { NotificationCenterProvider } from './notifications/NotificationCenterProvider';
import { toast } from './paToast';
import { queryClient } from './queryClient';
import { UserMenu } from './react-components/UserMenu';
import { usePageTitle } from './react-components/usePageTitle';
import SimulationProgress from './simulation-status/progress/SimulationProgress';
import ToastHistory from './toast-history/ToastHistory';
import 'react-toastify/dist/ReactToastify.css';
import './toast.css';
import EnvLabel from './EnvLabel';

dayjs.extend(utc);

const MUI_PRO_LICENSE_KEY =
  '28295685e74e802ad0b531b1680a795dTz03Nzc2ZDlmZC05ZWZlLTQwMGEtODg3Yi0xYjM4OWVlYTk4NTgsRT0xNzk3MjA2Mzk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTMsS1Y9Mw==';
LicenseInfo.setLicenseKey(MUI_PRO_LICENSE_KEY);

const AgentDesktop = lazy(() => import('./desktop/AgentDesktop.js'));

function AppFrame({
  accounts,
  env,
  handleLogoutClick,
}: Readonly<{
  accounts: ReturnType<typeof useMsal>['accounts'];
  env: EnvInfo | undefined;
  handleLogoutClick: () => void;
}>) {
  const theme = useTheme();
  // Small screens get the Agent chat only (rendered by AgentPanelProvider), no desktop.
  const agentOnly = useAgentOnlyLayout();
  if (agentOnly) return null;

  return (
    <Suspense fallback={<div />}>
      <Box sx={{ position: 'relative', height: '100vh', minWidth: 0, overflow: 'hidden' }}>
        <AgentDesktop
          shellActions={
            <Box
              onClick={(event) => event.stopPropagation()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <DesktopShellRouter>
                <EnvLabel compact envInfo={env} />
                <SimulationProgress />
                <AgentLauncherChip position="top-right" />
                <Box sx={getShellActionGroupSx(theme)}>
                  <NotificationCenter />
                  {accounts && accounts.length > 0 && accounts[0].name && (
                    <UserMenu
                      userName={accounts[0].name}
                      userEmail={accounts[0].username}
                      onLogout={handleLogoutClick}
                    />
                  )}
                </Box>
              </DesktopShellRouter>
            </Box>
          }
        />
      </Box>
    </Suspense>
  );
}

function App() {
  const { instance, accounts } = useMsal();

  const [env, setEnv] = useState<EnvInfo>();

  const handleLogoutClick = useCallback(async () => {
    // sessionStorage outlives the redirect; the next account must not see this one's Agent data.
    clearAgentTabSession();
    await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  // Ensure MSAL has an active account once we have at least one
  useEffect(() => {
    const account = accounts?.[0];
    if (!account) return;

    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(account);
    }
  }, [accounts, instance]);

  // Set the user for the Datadog RUM
  useEffect(() => {
    const activeAccount = instance.getActiveAccount() ?? accounts[0];
    if (!activeAccount) return;

    // biome-ignore lint/suspicious/noExplicitAny: datadog stuff
    (window as any).DD_RUM?.setUser({
      email: activeAccount.username,
      name: activeAccount.name,
    });
  }, [instance.getActiveAccount, accounts[0], instance]);

  useEffect(() => {
    if (env) {
      return;
    }

    const activeAccount = instance.getActiveAccount() ?? accounts[0];
    if (!activeAccount) {
      return;
    }

    getEnv()
      .then((res) => setEnv(res))
      .catch((e) => toast.error(`Failed to fetch environment: ${e}`));
  }, [instance.getActiveAccount, accounts, env, instance]);

  usePageTitle('PowerAlpha');

  return (
    <StyledEngineProvider injectFirst>
      <QueryClientProvider client={queryClient}>
        <UserPreferencesProvider>
          <PreferredThemeProvider>
            <AppToastContainers />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <HotkeysProvider appHotkeys={appHotkeys}>
                <DesktopProvider>
                  <DesktopShellRouter>
                    <ToastHistory />
                  </DesktopShellRouter>
                  <AgentPanelProvider>
                    <NotificationCenterProvider>
                      <AppFrame
                        accounts={accounts}
                        env={env}
                        handleLogoutClick={handleLogoutClick}
                      />
                    </NotificationCenterProvider>
                  </AgentPanelProvider>
                </DesktopProvider>
              </HotkeysProvider>
            </LocalizationProvider>
          </PreferredThemeProvider>
        </UserPreferencesProvider>
      </QueryClientProvider>
    </StyledEngineProvider>
  );
}

export default App;
